import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { EDIT, VIEW, IDLE, PENDING, REQUIREMENTS } from "const"
import { CcRequirement } from "server/models"
import { hasProjectAccess, StateType } from "client/utils"
import {
    PRM_CC_REQUIREMENT_REVIEW,
    PRM_CC_RESULTS_PDF,
    StoreType,
    CcRequirementReviewProps,

    LoadCcRequirementDictionaries,
    LoadCcApprovalLevels,
    LoadCcResponses,
    LoadAllProjectGroupUsers,
    LoadAllCcRequirements,
    LoadAllCcRequirementParentChild,
    LoadCcRequirementRevisions,
    LoadCcRequirementRevisionData,
    CreateCcRequirementRevision,
    PrepareRequirementsNestedIds,
    SortCcRequirement,

    setCcRequirementDictionariesLoaded,
    setProjectGroupUserList,
    setCcApprovalLevelList,
    setCcModuleApprovalLevelList,
    setCcResponseList,
    setCcModuleResponseList,

    setCcRequirementList,
    setCcRequirementParentChild,
    setCcRequirementNested,
    setCcRequirementRevisionList,
    setCcRequirementRevisionActive,
    setCcRequirementSort,
    setCcRequirementVisibleRequirementIds,
    setCcRequirementReviewListLoadState,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,

    LangReportWrapper,
    LangReportSwitcher,

    Table,
    SortRows,
    OnGetDisplayedIds,

    CheckRecentVersions,
} from "client/components"

import ExportToPDF from "client/pages/cc/exportToPDF"

import Columns from "./table/columns"
import TableBottom from "./table/bottom"
import PopupMenu from "./popupMenu"
import DisplayRequirementReviewColumns from "./displayColumns"
import Open from "./open"

const CcRequirementReviewList = ({
    requirementId = null,
    revisionId = null,
}: CcRequirementReviewProps) => {
    const tableClass = useSelector((state: StoreType) => state.ccRequirement.tableClass)
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseReviewRoute)
    const dictionariesLoaded = useSelector((state: StoreType) => state.ccRequirement.dictionariesLoaded)
    const listLoadState = useSelector((state: StoreType) => state.ccRequirement.reviewListLoadState)

    const requirementList = useSelector((state: StoreType) => state.ccRequirement.requirementList)
    const requirementParentChild = useSelector((state: StoreType) => state.ccRequirement.requirementParentChild)
    const requirementGroupedIds = useSelector((state: StoreType) => state.ccRequirement.requirementGroupedIds)
    const sortColumn = useSelector((state: StoreType) => state.ccRequirement.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.ccRequirement.sortOrder)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [dictionaryLoadState, setDictionaryLoadState] = useState<StateType>(IDLE)
    const [showExport, setShowExport] = useState(false)

    useEffect(() => {
        if (dictionariesLoaded) return
        setDictionaryLoadState(PENDING)

        LoadCcRequirementDictionaries(userData.accountId, project.id, dispatch).then(() => {
            setDictionaryLoadState(IDLE)
            dispatch(setCcRequirementDictionariesLoaded(true))
        })
    }, [dictionariesLoaded])

    useEffect(() => {
        dispatch(setCcRequirementReviewListLoadState(PENDING))
        Promise.all([
            // check recent versions for existing files
            CheckRecentVersions(project.id),

            // requirement approval levels
            LoadCcApprovalLevels(userData.accountId, project.id, 3)
            .then((res) => {
                dispatch(setCcApprovalLevelList(res.levelList))
                dispatch(setCcModuleApprovalLevelList({ 3: res.levelIds }))
            }),
            // requirement responses
            LoadCcResponses(userData.accountId, project.id, 4)
            .then((res) => {
                dispatch(setCcResponseList(res.responseList))
                dispatch(setCcModuleResponseList({ 4: res.responseIds }))
            }),
            // responsible responses
            LoadCcResponses(userData.accountId, project.id, 0)
            .then((res) => {
                dispatch(setCcResponseList(res.responseList))
                dispatch(setCcModuleResponseList({ 0: res.responseIds }))
            }),
            LoadAllProjectGroupUsers(userData.accountId, project.id)
            .then((res) => {
                dispatch(setProjectGroupUserList(res))
            }),
            LoadAllCcRequirementParentChild(project.id).then((res) => {
                dispatch(setCcRequirementParentChild(res))
            }),
            LoadAllCcRequirements(project.id).then((res) => {
                dispatch(setCcRequirementList(res))

                const promiseArray: Promise<any>[] = []
                Object.values(res).forEach((requirement) => {
                    promiseArray.push(
                        LoadCcRequirementRevisions(requirement.id).then((revisions) => {
                            // if revisions exist
                            if (revisions.length) {
                                const lastRevision = revisions[revisions.length - 1]
                                dispatch(setCcRequirementRevisionList(requirement.id, revisions))
                                dispatch(setCcRequirementRevisionActive(requirement.id, lastRevision))

                                return LoadCcRequirementRevisionData(requirement.id, lastRevision.id, dispatch)
                            }

                            // if no revisions - create new
                            return CreateCcRequirementRevision(requirement.id).then((newRevision) => {
                                dispatch(setCcRequirementRevisionList(requirement.id, [newRevision]))
                                dispatch(setCcRequirementRevisionActive(requirement.id, newRevision))

                                return LoadCcRequirementRevisionData(requirement.id, newRevision.id, dispatch)
                            })
                        }),
                    )
                })
                return Promise.all(promiseArray)
            }),
        ]).then(() => {
            dispatch(setCcRequirementReviewListLoadState(IDLE))
        })
    }, [])

    useEffect(() => {
        // open answer by url
        if (requirementId && revisionId && baseRoute) Open(baseRoute, requirementId, revisionId, dispatch)
    }, [requirementId, revisionId, baseRoute])

    useEffect(() => {
        const requirementsFiltered = SortCcRequirement(Object.values(requirementList), sortColumn, sortOrder, {})
        const tableGroupedIds = PrepareRequirementsNestedIds(requirementsFiltered, requirementParentChild)
        dispatch(setCcRequirementNested(tableGroupedIds))
    }, [requirementList, requirementParentChild, sortColumn, sortOrder])

    const onSort: SortRows = (column: keyof CcRequirement, order) => {
        dispatch(setCcRequirementSort(column, order))
    }

    const getDisplayedIds: OnGetDisplayedIds = (requirementIds: number[]) => {
        dispatch(setCcRequirementVisibleRequirementIds(requirementIds))
    }

    const editAccess = hasProjectAccess(PRM_CC_REQUIREMENT_REVIEW, EDIT)
    const viewPdfAccess = hasProjectAccess(PRM_CC_RESULTS_PDF, VIEW)

    const { columns, displayColumns } = Columns(editAccess)

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.requirementsReview}</PageTitle>
                {
                    viewPdfAccess && (
                        <LangReportWrapper>
                            <LangReportSwitcher />
                            <Button
                                variant="success"
                                disabled={dictionaryLoadState === PENDING || listLoadState === PENDING}
                                onClick={() => setShowExport(true)}
                            >
                                {lang.export}
                            </Button>
                        </LangReportWrapper>
                    )
                }
            </PageHeader>
            <PageContent>
                {
                    (
                        dictionaryLoadState === PENDING
                        || listLoadState === PENDING
                    ) && <Loader />
                }

                <Table
                    className={tableClass}
                    columns={columns}
                    dataList={requirementList}
                    dataGroupedIds={requirementGroupedIds}
                    onGetDisplayedIds={getDisplayedIds}
                    onSort={onSort}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="fit"
                    showPagination={false}
                    showTotal={false}
                    bottomContent={editAccess && <TableBottom />}
                />

                <PopupMenu />
                <DisplayRequirementReviewColumns
                    columns={columns.filter((column) => !!column.header)} // don't show management columns
                    displayColumns={displayColumns}
                />

                <ExportToPDF
                    show={showExport}
                    defaultReport={REQUIREMENTS}
                    onClose={() => setShowExport(false)}
                />
            </PageContent>
        </Container>
    )
}
export default CcRequirementReviewList
